import * as React      from 'react';
import Helmet          from 'react-helmet';
import { useLocation } from 'react-router-dom';

import type { MetaImage, MetaProps } from './meta.props';

const OG_LOCALE = 'ru_RU';

/**
 * Презентационный компонент — рендерит уже готовый SeoMeta с бэка (тот же
 * объект, что использует base.html.twig для холодного HTML). Никакой
 * нормализации/fallback-логики здесь быть не должно — она вся на бэке
 * (MetaBuilderService), чтобы данные гарантированно совпадали для Twig и React.
 */
/**
 * base.html.twig renders the same head tags for the cold HTML. react-helmet only manages its own tags
 * (data-react-helmet), so after a client-side navigation the server copies stayed next to the new ones —
 * e.g. robots "index, follow" from the first page next to "noindex, nofollow" of an empty catalog
 * filter. Once Helmet has written the head, the server copies of the tags it rendered are removed; tags
 * Helmet did not render (and pages without Meta) keep the server ones.
 */
const dropServerHeadDuplicates = (): void => {
  const selectors = new Set<string>();

  document.head.querySelectorAll('meta[data-react-helmet], link[data-react-helmet]').forEach((tag) => {
    for (const attribute of ['name', 'property', 'rel']) {
      const value = tag.getAttribute(attribute);
      if (value) {
        selectors.add(`${tag.tagName.toLowerCase()}[${attribute}="${value}"]:not([data-react-helmet])`);
        break;
      }
    }
  });

  selectors.forEach((selector) => {
    document.head.querySelectorAll(selector).forEach((tag) => tag.remove());
  });
};

const Meta: React.FC<MetaProps> = function(props) {
  const location = useLocation();

  const {
          article,
          canonical,
          description,
          image,
          images,
          isIndexed,
          keywords,
          price,
          title,
          type = 'website',
        } = props;

  const resolvedImages: MetaImage[] = images?.length ? images : (image?.src ? [image] : []);

  return (
    <>
      <Helmet onChangeClientState={dropServerHeadDuplicates}>
        <title>{title}</title>
        <meta
          content={title}
          property={'og:title'}
        />
        <meta
          content={description}
          name={'description'}
        />
        <meta
          content={description}
          property={'og:description'}
        />
        <meta
          content={keywords}
          name={'keywords'}
        />
        {canonical && (
          <link
            href={canonical}
            rel={'canonical'}
          />
        )}
        {typeof isIndexed === 'boolean' && (
          <meta
            content={isIndexed ? 'index, follow' : 'noindex, nofollow'}
            name={'robots'}
          />
        )}
        {/* react-helmet не умеет обрабатывать React.Fragment как прямого
            child (падает с "can't convert symbol to string" — тип фрагмента,
            Symbol(react.fragment), не входит в его VALID_TAG_NAMES), поэтому
            каждый tag для каждой картинки идёт отдельным элементом плоского
            массива, а не сгруппированным во фрагмент */}
        {resolvedImages.flatMap((img, index) => [
          <meta
            content={img.src}
            key={`image-src-${img.src ?? index}`}
            property={'og:image'}
          />,
          img.alt && (
            <meta
              content={img.alt}
              key={`image-alt-${img.src ?? index}`}
              property={'og:image:alt'}
            />
          ),
          img.type && (
            <meta
              content={img.type}
              key={`image-type-${img.src ?? index}`}
              property={'og:image:type'}
            />
          ),
          typeof img.width === 'number' && (
            <meta
              content={img.width.toString()}
              key={`image-width-${img.src ?? index}`}
              property={'og:image:width'}
            />
          ),
          typeof img.height === 'number' && (
            <meta
              content={img.height.toString()}
              key={`image-height-${img.src ?? index}`}
              property={'og:image:height'}
            />
          ),
        ].filter(Boolean))}
        {article?.section && (
          <meta
            content={article.section}
            property={'article:section'}
          />
        )}
        {article?.author && (
          <meta
            content={article.author}
            property={'article:author'}
          />
        )}
        {article?.publishedTime && (
          <meta
            content={article.publishedTime}
            property={'article:published_time'}
          />
        )}
        {article?.modifiedTime && (
          <meta
            content={article.modifiedTime}
            property={'article:modified_time'}
          />
        )}
        {typeof price?.amount === 'number' && [
          <meta
            content={price.amount.toString()}
            key={'price-amount'}
            property={'product:price:amount'}
          />,
          <meta
            content={price.currency ?? 'BYN'}
            key={'price-currency'}
            property={'product:price:currency'}
          />,
        ]}
        {/* window недоступен при SSR-рендере React (node/ssr-server.js);
            для холодного HTML og:url уже корректно отдаёт base.html.twig */}
        {!isSSR() && (
          <meta
            content={`${window.location.origin}${location.pathname}${location.search}`}
            property={'og:url'}
          />
        )}
        <meta
          content={type}
          property={'og:type'}
        />
        <meta
          content={OG_LOCALE}
          property={'og:locale'}
        />
        <meta
          content={'summary_large_image'}
          name={'twitter:card'}
        />
        <meta
          content={title}
          name={'twitter:title'}
        />
        <meta
          content={description}
          name={'twitter:description'}
        />
        {resolvedImages[0]?.src && (
          <meta
            content={resolvedImages[0].src}
            name={'twitter:image'}
          />
        )}
      </Helmet>
    </>
  );
};

export default Meta;
